/// <amd-module name="Notifications/Tools/Components/EventTransformPivot" />
import * as React from "react";
// VSSUI
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
// Notifications
import { INotificationRestClient} from "Notifications/Common/RestClient/Notification";
import { NotificationEventType, EventTransformResult, EventTransformRequest } from "Notifications/Common/Contracts/Notification";
// VSS
import { PickList, IPickListSelection,  PickListDropdown } from "VSSUI/PickList";
import { PivotBar, PivotBarItem } from "VSSUI/PivotBar";
import { ContributedPivotBarItemProvider, IPivotBarItemSpec } from "VSS/Features/Frame/ContributedPivotBarItemProvider"
import { VssComponent } from "VSS/Platform/Layout";
import { SystemInputs } from "Notifications/Tools/Components/SystemInputs";

interface IEventTransformPivotState {
    eventType?: NotificationEventType;
    eventPayload?: string;
    isTransforming?: boolean;
    invalidEventMessage?: string;
    transformResult?: EventTransformResult;
}

class EventTransformPivot extends VssComponent<{}, IEventTransformPivotState> {

    private systemInputs: { [key: string]: string };
    
    public constructor(props: any) {
        super(props);
        
        this.systemInputs = {};
        
        this.state = {
            isTransforming: false,
            invalidEventMessage: '',
        }
    } 
    
    private eventTypes: NotificationEventType[];
    
    private async loadEventTypes(): Promise<NotificationEventType[]> {
        if (this.eventTypes == null) {
            const client = this.context.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");
            this.eventTypes = await client.listEventTypes();
        } 
        
        return this.eventTypes;
    }
    
    private onEventTypeChanged = (selection: IPickListSelection) => {
        const newEventType: NotificationEventType = selection.selectedItems.length > 0 ? selection.selectedItems[0] : null;
        this.updateSelectedEvent(newEventType, this.state.eventPayload);
    }
    
    private onEventPayloadChanged = (event: React.ChangeEvent<HTMLInputElement>, newEventPayload: string) => {
        this.updateSelectedEvent(this.state.eventType, newEventPayload);
    }
    
    private updateSelectedEvent = (eventType: NotificationEventType | undefined, eventPayload: string | undefined) => {
        this.setState({
            eventType: eventType,
            eventPayload: eventPayload,
        });
    }
    
    private async onTransformClick() {
        let transformResult: EventTransformResult;
        const { eventType, eventPayload } = this.state;
        
        if (eventType && eventPayload) {
            this.setState({
                isTransforming: true,
                invalidEventMessage: undefined,
                transformResult: undefined
            });
            const serviceInstanceType = eventType.eventPublisher.subscriptionManagementInfo.serviceInstanceType;            
            const client = this.context.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient", { serviceInstanceType: serviceInstanceType });
            const transformRequest: EventTransformRequest = {
                eventType: eventType.id,
                eventPayload: eventPayload,
                systemInputs: this.systemInputs
            };
            
            try {
                transformResult = await client.transformEvent(transformRequest);
            } catch (e) {
                transformResult = {
                    content: e.message,
                    data: {}, 
                    systemInputs: {}
                };
                
            }
            
            this.setState({
                transformResult,
                isTransforming: false
            });
        } else {
            this.setState({
                isTransforming: false,
                invalidEventMessage: "Select an event type and provide payload"
            });
        }
    }
    
    private updateSystemInputs = (systemInputs: {[key: string]: string}): void => {
        this.systemInputs = systemInputs;
    };
    
    public render(): JSX.Element {
        const {eventType = null, eventPayload, isTransforming, transformResult} = this.state;
        let previewPivotBar: JSX.Element | undefined;

        if (!isTransforming) {
            const previewContent = transformResult ? transformResult.content : '';
            const data = transformResult ? transformResult.data : {};
            previewPivotBar = (
                <PivotBar className={"flex-grow"}>
                    <PivotBarItem
                        itemKey="preview"
                        name="Preview">
                        <iframe
                            id={"preview-frame"}
                            srcDoc={previewContent}/>
                    </PivotBarItem>
                    <PivotBarItem
                        itemKey="source"
                        name="Source">
                        <pre>{previewContent}</pre>
                    </PivotBarItem>
                    <PivotBarItem
                        itemKey="data"
                        name="Data">
                        <pre>{JSON.stringify(data, null, 2)}</pre>
                    </PivotBarItem>
                </PivotBar>
            );
        }
        
        return (
            <div className="event-transform-pivot flex-grow">
                <div className="event-selection-area">
                    <h4>Select the type of event</h4>
                        <PickListDropdown
                            isSearchable
                            searchTextPlaceholder={'search event'}
                            searchNoResultsText={'no event match'}
                            disabled={isTransforming}
                            selectedItems={
                                (eventType ? [ eventType ] : [] )
                            }
                            onSelectionChanged={(selection) => { this.onEventTypeChanged(selection); }}
                            getPickListItems={() => { return this.loadEventTypes(); }}
                            getListItem={(eventType: NotificationEventType) => {
                                return {
                                    name: eventType.name || eventType.id,
                                    key: eventType.id
                                }
                            }}
                        />
                    <h4>Specify the event payload</h4>
                    <TextField
                        multiline
                        rows={30}
                        spellCheck={false}
                        disabled={isTransforming}
                        required={true}
                        onChange={this.onEventPayloadChanged}
                        value={eventPayload}/>

                    <SystemInputs onChange={this.updateSystemInputs}/>
                    
                    <br/>
                    <button 
                        disabled={isTransforming || !(this.state.eventType && this.state.eventPayload)}
                        onClick={() => this.onTransformClick()}>
                        Transform
                    </button>                        
                </div>

                <div className="preview-area">
                    { isTransforming  ? 
                        <Spinner size={ SpinnerSize.large } label="Transforming event..." />
                      :
                        previewPivotBar
                    }
                </div>
            </div>
        );
    }
}

VssComponent.register('eventTransformComponent', EventTransformPivot);
